// Modal component without external dependencies
import { useEffect, useState } from 'react';

interface NodeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeName: string;
  nodeDescription: string;
  onSave: (description: string) => void;
}

const NodeEditModal = ({ isOpen, onClose, nodeName, nodeDescription, onSave }: NodeEditModalProps) => {
  const [description, setDescription] = useState(nodeDescription);

  useEffect(() => {
    // Reset description when modal opens with new node
    setDescription(nodeDescription);
  }, [nodeDescription, isOpen]);

  const handleSave = () => {
    onSave(description);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Background overlay */}
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />

      {/* Modal container */}
      <div className="relative bg-custom-gray border border-gray-700 rounded-lg shadow-xl max-w-md w-full z-10">
        <div className="flex justify-between items-center border-b border-gray-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-custom-aliceBlue">Edit Node: {nodeName}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5">
          <label className="block mb-2 text-sm font-medium text-gray-300">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md text-gray-200 focus:border-primary-400 focus:outline-none transition-colors resize-none"
            rows={5}
            placeholder="Enter a description for this node..."
          />
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-primary-400 hover:bg-primary-500 text-gray-900 font-medium rounded-md transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeEditModal;
